import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { EngineeringHttpService } from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EditablePage } from '@shared/components';
import { EngineeringActions } from '@store/actions';
import {
  DDVSelectors,
  EngineeringSelectors,
  ProjectSelectors,
  UserSelectors,
} from '@store/selectors';
import { EngineeringSummary } from './components';

type EngineeringSummaryTabProps = RouterProps & {
  isActive?: boolean;
};

export const EngineeringSummaryTab: FC<EngineeringSummaryTabProps> = ({
  isActive,
}) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    EngineeringSelectors.getTabLoadingState('summary'),
  );
  const isDataLoaded = useSelector(
    EngineeringSelectors.getTabLoadedState('summary'),
  );
  const projectType = useSelector(EngineeringSelectors.getProjectType);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringBrowse,
      Tab.Summary,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        EngineeringActions.setTabLoading.done({
          tabName: 'summary',
          isLoading: true,
        }),
      );

      HttpService.getHttpRequests(EngineeringHttpService)
        .getEngineeringSummary(projectBundleID)
        .catch(() =>
          dispatch(
            EngineeringActions.setTabError.done({
              tabName: 'summary',
              hasError: true,
            }),
          ),
        );
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  if (isLoading || !isDataLoaded) {
    return <Spin size="large" />;
  }

  return (
    <EditablePage
      editableComponent={EngineeringSummary}
      canUserEdit={
        canEdit &&
        projectTypePermission &&
        canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
    />
  );
};
